import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, UserRound } from 'lucide-react';
import { FaLinkedin } from 'react-icons/fa6';
import { useFounderPhoto } from '../hooks/useFounderPhoto';

const PHOTO_FALLBACK = '/images/brand/logo-image.webp';

/** « Qui suis-je ? » : présentation du fondateur, à la première personne. */
const FounderSection: React.FC = () => {
  const { founder, loading, photoUrl: founderPhoto, handlePhotoError } = useFounderPhoto();
  // Photo officielle, puis photo du compte, puis logo
  const photoUrl = founderPhoto ?? PHOTO_FALLBACK;

  return (
    <section id="qui-suis-je" className="section-spacing border-t border-line bg-canvas">
      <div className="container-site">
        <div className="section-title-spacing text-center">
          <div className="section-subtitle-badge">
            <UserRound size={14} aria-hidden />
            <span>Votre interlocuteur</span>
          </div>
          <h2 className="heading-section">
            Qui <span className="text-accent-soft">suis-je</span>&nbsp;?
          </h2>
        </div>

        <motion.div
          className="card mx-auto grid max-w-4xl grid-cols-1 overflow-hidden md:grid-cols-[minmax(0,280px)_1fr]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        >
          <div className="relative aspect-square border-b border-line bg-surface-2 md:aspect-auto md:border-b-0 md:border-r">
            {loading ? (
              <div className="absolute inset-0 animate-pulse bg-surface-2" aria-hidden />
            ) : (
              <img
                src={photoUrl}
                alt="Lucas Olive, fondateur de SOS IT Services"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
                onError={handlePhotoError}
              />
            )}
          </div>

          <div className="flex flex-col gap-4 p-6 md:p-8">
            <div>
              <h3 className="text-xl font-extrabold tracking-[-0.01em] text-fg md:text-2xl">Lucas Olive, fondateur</h3>
            </div>
            <p className="leading-[1.65] text-fg-muted">
              Ingénieur IT depuis plus de 15 ans (développement logiciel, informatique bancaire internationale, projets
              export), je mets cette expérience au service des particuliers et des pros de la région, avec la même
              exigence.
            </p>
            <p className="leading-[1.65] text-fg-muted">
              Contrairement à une boutique, je me déplace chez vous et vous avez toujours le même interlocuteur, du
              diagnostic au suivi.
            </p>
            <p className="flex items-start gap-2 text-fg">
              <MapPin size={18} className="mt-0.5 shrink-0 text-accent-soft" aria-hidden />
              <span>Basé à Viry, à 10&nbsp;min de Genève et de Saint-Julien.</span>
            </p>
            {founder?.linkedin && (
              <a
                href={founder.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary mt-2 self-start !text-sm"
              >
                <FaLinkedin size={16} className="text-accent-soft" aria-hidden />
                Mon profil LinkedIn
              </a>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FounderSection;
